import { Document, Page, StyleSheet, Text, View } from '@react-pdf/renderer'
import { RoiByPlanType, SegmentProcessResult } from '../types'
import { formatRoiInsight, formatRoiValue } from '../utils/roi-copy'

const c = {
  dark: '#1a1a2e',
  text: '#2d2d3f',
  textLight: '#a0a0b8',
  textDark: '#374151',
  muted: '#8b8ba3',
  accent: '#7c3aed',
  accentLight: '#ede9fe',
  green: '#059669',
  greenLight: '#d1fae5',
  red: '#dc2626',
  redLight: '#fee2e2',
  bg: '#fafafa',
  card: '#ffffff',
  border: '#eeeef2'
}

const s = StyleSheet.create({
  page: {
    padding: 48,
    fontFamily: 'Helvetica',
    backgroundColor: c.bg
  },
  // Hero header
  hero: {
    backgroundColor: c.dark,
    borderRadius: 8,
    padding: 28,
    marginBottom: 28
  },
  heroLabel: {
    fontSize: 9,
    color: c.accent,
    textTransform: 'uppercase',
    letterSpacing: 2,
    marginBottom: 8,
    fontWeight: 'bold'
  },
  heroTitle: {
    fontSize: 26,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 6
  },
  heroSub: {
    fontSize: 11,
    color: '#a0a0b8'
  },
  // Insight callout
  insight: {
    backgroundColor: c.accentLight,
    borderRadius: 6,
    padding: 16,
    marginBottom: 24,
    borderLeftWidth: 3,
    borderLeftColor: c.accent
  },
  insightLabel: {
    fontSize: 8,
    color: c.accent,
    textTransform: 'uppercase',
    letterSpacing: 1.5,
    fontWeight: 'bold',
    marginBottom: 6
  },
  insightText: {
    fontSize: 11,
    color: c.text,
    lineHeight: 1.6
  },
  // Section
  section: { marginBottom: 24 },
  sectionTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: c.muted,
    textTransform: 'uppercase',
    letterSpacing: 1.5,
    marginBottom: 12
  },
  // KPI cards
  kpiRow: { flexDirection: 'row', gap: 10 },
  kpiCard: {
    flex: 1,
    backgroundColor: c.card,
    borderRadius: 6,
    padding: 16,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: c.border
  },
  kpiLabel: {
    fontSize: 7,
    color: c.muted,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: 6,
    textAlign: 'center'
  },
  kpiValue: {
    fontSize: 16,
    fontWeight: 'bold',
    color: c.text,
    textAlign: 'center'
  },
  kpiSub: {
    fontSize: 8,
    color: c.muted,
    marginTop: 4,
    textAlign: 'center'
  },
  // Ticket medio highlight
  tmHighlight: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 8
  },
  tmCard: {
    flex: 1,
    borderRadius: 6,
    padding: 18,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: c.border
  },
  tmValue: {
    fontSize: 18,
    fontWeight: 'bold',
    textAlign: 'center'
  },
  tmLabel: {
    fontSize: 7,
    color: c.muted,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: 8,
    textAlign: 'center'
  },
  tmDiffLabel: {
    fontSize: 8,
    color: c.muted,
    marginTop: 4,
    textAlign: 'center'
  },
  // Ranking
  rankingWrap: { flexDirection: 'row', gap: 14 },
  rankingCol: {
    flex: 1,
    borderRadius: 6,
    padding: 14,
    borderWidth: 1,
    borderColor: c.border
  },
  rankingHeader: {
    fontSize: 9,
    fontWeight: 'bold',
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 1
  },
  rankingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#f5f5f8'
  },
  rankingPos: { fontSize: 9, fontWeight: 'bold', width: 20 },
  rankingName: { fontSize: 9, color: c.text, flex: 1 },
  rankingRoi: {
    fontSize: 9,
    fontWeight: 'bold',
    width: 36,
    textAlign: 'right'
  },
  rankingRev: { fontSize: 8, color: c.muted, width: 80, textAlign: 'right' },
  // Footer
  footer: {
    position: 'absolute',
    bottom: 24,
    left: 48,
    right: 48,
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderTopWidth: 1,
    borderTopColor: c.border,
    paddingTop: 8
  },
  footerText: { fontSize: 7, color: '#c4c4d4' },
  // Divider
  divider: { height: 1, backgroundColor: c.border, marginVertical: 20 }
})

function fmt(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0)
}

function fmtCompact(value: number): string {
  const abs = Math.abs(value || 0)
  if (abs >= 1_000_000_000) {
    return `R$ ${(value / 1_000_000_000).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} bi`
  }
  if (abs >= 1_000_000) {
    return `R$ ${(value / 1_000_000).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} mi`
  }
  return fmt(value)
}

export interface PdfSections {
  insight: boolean
  resultados: boolean
  ticketMedio: boolean
  roiPlano: boolean
  perfilComprador: boolean
  ranking: boolean
}

export interface PdfCards {
  receitaTotal: boolean
  receitaInfluenciada: boolean
  roiMedio: boolean
  tmComWidde: boolean
  tmSemWidde: boolean
  tmDiferenca: boolean
  rankingTop: boolean
  rankingBottom: boolean
}

export interface FilteredPdfData {
  totals: {
    totalOrders: number
    totalRevenue: number
    influencedOrders: number
    influencedRevenue: number
    directOrders: number
  }
  ticketMedio: {
    avgTicket: number
    avgTicketInfluenced: number
    avgTicketAttributedOrders: number
    avgTicketOrganic: number
    diffPercent: number | null
  }
  roi: {
    avgRoi: number | null
    companiesWithRoi: number
    byPlanType: RoiByPlanType[]
  }
  averages: {
    avgOrders: number
    avgRevenue: number
    avgInfluencedOrders: number
    avgInfluencedRevenue: number
    avgConversionRate: number
  }
  averagesFiltered: {
    avgOrders: number
    avgRevenue: number
    avgInfluencedOrders: number
    avgInfluencedRevenue: number
    avgConversionRate: number
    companiesCount: number
  }
  companiesProcessed: number
  ranking: {
    top5: SegmentProcessResult['companies']
    bottom5: SegmentProcessResult['companies']
    sortedByRoi: SegmentProcessResult['companies']
  }
}

interface Props {
  result: SegmentProcessResult
  startDate: string
  endDate: string
  selectedPlanTypes?: string[]
  sections?: PdfSections
  cards?: PdfCards
  selectedInsights?: boolean[]
  filteredData?: FilteredPdfData
}

export function SegmentReportPdf({
  result,
  startDate,
  endDate,
  selectedPlanTypes,
  sections,
  cards,
  selectedInsights,
  filteredData
}: Props) {
  const sec = sections ?? {
    insight: true,
    resultados: true,
    ticketMedio: true,
    roiPlano: true,
    perfilComprador: true,
    ranking: true
  }
  const crd = cards ?? {
    receitaTotal: true,
    receitaInfluenciada: true,
    roiMedio: true,
    tmComWidde: true,
    tmSemWidde: true,
    tmDiferenca: true,
    rankingTop: true,
    rankingBottom: true
  }
  const { companies, segment } = result

  // Use filteredData when available (plan-type filtering), otherwise fall back to result data
  const totals = filteredData?.totals ?? result.totals
  const ticketMedio = filteredData?.ticketMedio ?? result.ticketMedio
  const averages = filteredData?.averages ?? result.averages
  const averagesFiltered =
    filteredData?.averagesFiltered ?? result.averagesFiltered
  const roi = filteredData?.roi ?? result.roi
  const companiesProcessed =
    filteredData?.companiesProcessed ?? result.companiesProcessed

  const buyerProfile = result.buyerProfile

  const hasFilteredDiff =
    averagesFiltered.companiesCount > 0 &&
    averagesFiltered.companiesCount < companiesProcessed

  const companiesWithRoi = filteredData?.ranking
    ? filteredData.ranking.sortedByRoi
    : companies.filter(co => co.success && co.roi !== null)
  const sortedByRoi = filteredData?.ranking
    ? filteredData.ranking.sortedByRoi
    : [...companiesWithRoi].sort((a, b) => (b.roi ?? 0) - (a.roi ?? 0))
  const top5 = filteredData?.ranking
    ? filteredData.ranking.top5
    : sortedByRoi.slice(0, 5)
  const bottom5 = filteredData?.ranking
    ? filteredData.ranking.bottom5
    : sortedByRoi.length > 5
      ? sortedByRoi.slice(-5).reverse()
      : []

  const fmtDate = (d: string) => {
    const [y, m, day] = d.split('-')
    return `${day}/${m}/${y}`
  }
  const days =
    Math.round(
      (new Date(endDate).getTime() - new Date(startDate).getTime()) /
        (1000 * 60 * 60 * 24)
    ) + 1

  // Build insight narrative
  const insights: string[] = []
  if (averages.avgConversionRate > 0) {
    insights.push(
      `Em ${days} dias, ${averages.avgConversionRate.toFixed(1)}% dos pedidos do segmento "${segment}" foram influenciados pela Widde.`
    )
  }
  if (ticketMedio.diffPercent !== null && ticketMedio.diffPercent > 0) {
    insights.push(
      `Clientes que interagiram com a Widde gastam em media ${ticketMedio.diffPercent.toFixed(0)}% a mais por pedido (${fmt(ticketMedio.avgTicketAttributedOrders)} vs ${fmt(ticketMedio.avgTicketOrganic)}).`
    )
  }
  if (roi.avgRoi !== null && roi.avgRoi > 1) {
    insights.push(
      formatRoiInsight(roi.avgRoi)
    )
  }

  return (
    <Document>
      <Page size="A4" style={s.page}>
        {/* Hero */}
        <View style={s.hero}>
          <Text style={s.heroLabel}>Relatorio de Performance</Text>
          <Text style={s.heroTitle}>{segment}</Text>
          <Text style={s.heroSub}>
            {fmtDate(startDate)} a {fmtDate(endDate)} | {days} dias |{' '}
            {companiesProcessed} empresas analisadas
          </Text>
        </View>

        {/* Insight */}
        {(() => {
          const filteredInsights = selectedInsights
            ? insights.filter((_, i) => selectedInsights[i] !== false)
            : insights
          return sec.insight && filteredInsights.length > 0 ? (
            <View style={s.insight}>
              <Text style={s.insightLabel}>Destaque</Text>
              <Text style={s.insightText}>{filteredInsights.join(' ')}</Text>
            </View>
          ) : null
        })()}

        {/* Resultados Gerais */}
        {sec.resultados && (
          <View style={s.section}>
            <Text style={s.sectionTitle}>Resultados Gerais</Text>
            <View style={s.kpiRow}>
              {crd.receitaTotal && (
                <View style={s.kpiCard}>
                  <Text style={s.kpiLabel}>Receita Total</Text>
                  <Text style={s.kpiValue}>
                    {fmtCompact(totals.totalRevenue)}
                  </Text>
                  <Text style={s.kpiSub}>{totals.totalOrders} pedidos</Text>
                </View>
              )}
              {crd.receitaInfluenciada && (
                <View
                  style={{
                    ...s.kpiCard,
                    borderColor: c.accent,
                    borderWidth: 1.5
                  }}
                >
                  <Text style={{ ...s.kpiLabel, color: c.accent }}>
                    Receita Influenciada
                  </Text>
                  <Text style={{ ...s.kpiValue, color: c.accent }}>
                    {fmtCompact(totals.influencedRevenue)}
                  </Text>
                  <Text style={s.kpiSub}>
                    {totals.influencedOrders} pedidos
                  </Text>
                </View>
              )}
              {crd.roiMedio && (
                <View
                  style={{
                    ...s.kpiCard,
                    backgroundColor:
                      roi.avgRoi !== null && roi.avgRoi >= 1
                        ? c.greenLight
                        : c.card,
                    borderColor:
                      roi.avgRoi !== null && roi.avgRoi >= 1
                        ? c.green
                        : c.border,
                    borderWidth:
                      roi.avgRoi !== null && roi.avgRoi >= 1 ? 1.5 : 1
                  }}
                >
                  <Text style={s.kpiLabel}>ROI Medio</Text>
                  <Text
                    style={{
                      ...s.kpiValue,
                      color:
                        roi.avgRoi !== null
                          ? roi.avgRoi >= 0
                            ? c.green
                            : c.red
                          : c.muted
                    }}
                  >
                    {roi.avgRoi !== null
                      ? `${formatRoiValue(roi.avgRoi)}x`
                      : '\u2014'}
                  </Text>
                  <Text style={s.kpiSub}>
                    {roi.companiesWithRoi} empresas com ROI
                  </Text>
                </View>
              )}
            </View>
          </View>
        )}

        {/* Filtered note */}
        {sec.resultados && hasFilteredDiff && (
          <View
            style={{
              backgroundColor: '#f8fafc',
              borderRadius: 4,
              padding: 10,
              marginBottom: 16,
              borderWidth: 1,
              borderColor: c.border
            }}
          >
            <Text style={{ fontSize: 9, color: c.muted }}>
              {companiesProcessed - averagesFiltered.companiesCount} empresa(s)
              sem pedidos no periodo. Media considerando apenas{' '}
              {averagesFiltered.companiesCount} empresas com pedidos:{' '}
              {averagesFiltered.avgOrders.toFixed(1)} pedidos/empresa |{' '}
              {fmtCompact(averagesFiltered.avgRevenue)} receita/empresa |{' '}
              {averagesFiltered.avgInfluencedOrders.toFixed(1)}{' '}
              influenciados/empresa
            </Text>
          </View>
        )}

        {/* Ticket Medio */}
        {sec.ticketMedio && (
          <View style={s.section}>
            <Text style={s.sectionTitle}>Ticket Medio: Widde vs Organico</Text>
            <View style={s.tmHighlight}>
              {crd.tmComWidde && (
                <View
                  style={{
                    ...s.tmCard,
                    backgroundColor: c.accentLight,
                    borderColor: c.accent,
                    borderWidth: 1.5
                  }}
                >
                  <Text style={{ ...s.tmLabel, color: c.accent }}>
                    Com Widde
                  </Text>
                  <Text style={{ ...s.tmValue, color: c.accent }}>
                    {fmt(ticketMedio.avgTicketAttributedOrders)}
                  </Text>
                </View>
              )}
              {crd.tmSemWidde && (
                <View style={{ ...s.tmCard, backgroundColor: c.card }}>
                  <Text style={s.tmLabel}>Sem Widde</Text>
                  <Text style={{ ...s.tmValue, color: c.text }}>
                    {fmt(ticketMedio.avgTicketOrganic)}
                  </Text>
                </View>
              )}
              {crd.tmDiferenca && (
                <View
                  style={{
                    ...s.tmCard,
                    backgroundColor:
                      ticketMedio.diffPercent !== null &&
                      ticketMedio.diffPercent >= 0
                        ? c.greenLight
                        : c.redLight,
                    borderColor:
                      ticketMedio.diffPercent !== null &&
                      ticketMedio.diffPercent >= 0
                        ? c.green
                        : c.red,
                    borderWidth: 1.5
                  }}
                >
                  <Text style={s.tmLabel}>Diferenca</Text>
                  <Text
                    style={{
                      ...s.tmValue,
                      color:
                        ticketMedio.diffPercent !== null
                          ? ticketMedio.diffPercent >= 0
                            ? c.green
                            : c.red
                          : c.muted
                    }}
                  >
                    {ticketMedio.diffPercent !== null
                      ? `${ticketMedio.diffPercent >= 0 ? '+' : ''}${ticketMedio.diffPercent.toFixed(1)}%`
                      : '\u2014'}
                  </Text>
                  <Text style={s.tmDiffLabel}>por pedido</Text>
                </View>
              )}
            </View>
          </View>
        )}

        {/* ROI por Plano */}
        {sec.roiPlano &&
          (() => {
            const filteredPlans =
              selectedPlanTypes && selectedPlanTypes.length > 0
                ? roi.byPlanType.filter(p =>
                    selectedPlanTypes.includes(p.planType)
                  )
                : roi.byPlanType
            if (filteredPlans.length === 0) return null
            return (
              <View style={s.section}>
                <Text style={s.sectionTitle}>ROI por Plano</Text>
                <View style={s.kpiRow}>
                  {filteredPlans.map(p => (
                    <View
                      key={p.planType}
                      style={{
                        ...s.kpiCard,
                        borderColor:
                          p.avgRoi !== null && p.avgRoi >= 1
                            ? c.green
                            : c.border,
                        borderWidth:
                          p.avgRoi !== null && p.avgRoi >= 1 ? 1.5 : 1
                      }}
                    >
                      <Text style={s.kpiLabel}>{p.planType}</Text>
                      <Text
                        style={{
                          ...s.kpiValue,
                          fontSize: 18,
                          color:
                            p.avgRoi !== null
                              ? p.avgRoi >= 0
                                ? c.green
                                : c.red
                              : c.muted
                        }}
                      >
                        {p.avgRoi !== null
                          ? `${formatRoiValue(p.avgRoi)}x`
                          : '\u2014'}
                      </Text>
                      <Text style={s.kpiSub}>{p.companiesCount} empresas</Text>
                    </View>
                  ))}
                </View>
              </View>
            )
          })()}

        {/* Perfil do comprador */}
        {sec.perfilComprador &&
          buyerProfile &&
          buyerProfile.withVideo.newUsers +
            buyerProfile.withVideo.returningUsers >
            0 && (
            <View style={s.section}>
              <Text style={s.sectionTitle}>Perfil do Comprador</Text>
              <Text
                style={{ fontSize: 7, color: c.textLight, marginBottom: 6 }}
              >
                Compradores novos vs retornantes: com Widde vs sem Widde (GA4)
              </Text>
              <View style={{ flexDirection: 'row', gap: 8 }}>
                <View
                  style={{
                    flex: 1,
                    backgroundColor: c.greenLight,
                    borderRadius: 6,
                    padding: 8,
                    borderWidth: 1,
                    borderColor: c.green
                  }}
                >
                  <Text
                    style={{
                      fontSize: 7,
                      color: c.textLight,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      marginBottom: 2
                    }}
                  >
                    Com Widde
                  </Text>
                  <Text
                    style={{ fontSize: 16, fontWeight: 700, color: c.green }}
                  >
                    {buyerProfile.withVideo.returningPercent !== null
                      ? `${buyerProfile.withVideo.returningPercent.toFixed(1)}%`
                      : '—'}{' '}
                    ret.
                  </Text>
                  <Text style={{ fontSize: 6, color: c.textLight }}>
                    {buyerProfile.withVideo.newUsers +
                      buyerProfile.withVideo.returningUsers}{' '}
                    compras
                  </Text>
                </View>
                <View
                  style={{
                    flex: 1,
                    backgroundColor: '#f5f5f5',
                    borderRadius: 6,
                    padding: 8,
                    borderWidth: 1,
                    borderColor: '#d1d5db'
                  }}
                >
                  <Text
                    style={{
                      fontSize: 7,
                      color: c.textLight,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      marginBottom: 2
                    }}
                  >
                    Sem Widde
                  </Text>
                  <Text
                    style={{ fontSize: 16, fontWeight: 700, color: c.textDark }}
                  >
                    {buyerProfile.withoutVideo.returningPercent !== null
                      ? `${buyerProfile.withoutVideo.returningPercent.toFixed(1)}%`
                      : '—'}{' '}
                    ret.
                  </Text>
                  <Text style={{ fontSize: 6, color: c.textLight }}>
                    {buyerProfile.withoutVideo.newUsers +
                      buyerProfile.withoutVideo.returningUsers}{' '}
                    compras
                  </Text>
                </View>
                {buyerProfile.diffReturningPercent !== null && (
                  <View
                    style={{
                      flex: 1,
                      backgroundColor:
                        buyerProfile.diffReturningPercent > 0
                          ? c.greenLight
                          : c.redLight,
                      borderRadius: 6,
                      padding: 8,
                      borderWidth: 1,
                      borderColor:
                        buyerProfile.diffReturningPercent > 0 ? c.green : c.red
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 7,
                        color: c.textLight,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        marginBottom: 2
                      }}
                    >
                      Diferenca
                    </Text>
                    <Text
                      style={{
                        fontSize: 16,
                        fontWeight: 700,
                        color:
                          buyerProfile.diffReturningPercent > 0
                            ? c.green
                            : c.red
                      }}
                    >
                      {buyerProfile.diffReturningPercent > 0 ? '+' : ''}
                      {buyerProfile.diffReturningPercent.toFixed(0)}pp
                    </Text>
                    <Text style={{ fontSize: 6, color: c.textLight }}>
                      {buyerProfile.diffReturningPercent > 0
                        ? 'Widde fideliza mais'
                        : 'menos retornantes'}
                    </Text>
                  </View>
                )}
              </View>
            </View>
          )}

        {/* Ranking */}
        {sec.ranking && companiesWithRoi.length > 0 && (
          <View style={s.section}>
            <Text style={s.sectionTitle}>Ranking por ROI</Text>
            <View style={s.rankingWrap}>
              {crd.rankingTop && (
                <View
                  style={{
                    ...s.rankingCol,
                    backgroundColor: c.greenLight,
                    borderColor: c.green
                  }}
                >
                  <Text style={{ ...s.rankingHeader, color: c.green }}>
                    Melhores Resultados
                  </Text>
                  {top5.map((co, i) => (
                    <View key={co.companyKey} style={s.rankingRow}>
                      <Text style={{ ...s.rankingPos, color: c.green }}>
                        {i + 1}.
                      </Text>
                      <Text style={s.rankingName}>{co.companyTitle}</Text>
                      <Text style={{ ...s.rankingRoi, color: c.green }}>
                        {formatRoiValue(co.roi!)}x
                      </Text>
                      <Text style={s.rankingRev}>
                        {fmtCompact(co.influencedRevenue)}
                      </Text>
                    </View>
                  ))}
                </View>
              )}
              {crd.rankingBottom && bottom5.length > 0 && (
                <View
                  style={{
                    ...s.rankingCol,
                    backgroundColor: c.redLight,
                    borderColor: c.red
                  }}
                >
                  <Text style={{ ...s.rankingHeader, color: c.red }}>
                    Oportunidades de Melhoria
                  </Text>
                  {bottom5.map((co, i) => (
                    <View key={co.companyKey} style={s.rankingRow}>
                      <Text style={{ ...s.rankingPos, color: c.red }}>
                        {sortedByRoi.length - bottom5.length + i + 1}.
                      </Text>
                      <Text style={s.rankingName}>{co.companyTitle}</Text>
                      <Text
                        style={{
                          ...s.rankingRoi,
                          color: co.roi! >= 0 ? c.green : c.red
                        }}
                      >
                        {formatRoiValue(co.roi!)}x
                      </Text>
                      <Text style={s.rankingRev}>
                        {fmtCompact(co.influencedRevenue)}
                      </Text>
                    </View>
                  ))}
                </View>
              )}
            </View>
          </View>
        )}

        {/* Footer */}
        <View style={s.footer}>
          <Text style={s.footerText}>Widde Video Commerce</Text>
          <Text style={s.footerText}>
            Relatorio gerado automaticamente | {fmtDate(startDate)} -{' '}
            {fmtDate(endDate)}
          </Text>
        </View>
      </Page>
    </Document>
  )
}
